import React, { useCallback, useEffect, useRef, useState } from "react";
import closeIcon from "../../assets/cross.svg";
import { schoolService } from "../../services/schoolService";
import { inviteEmailError } from "../../lib/email";

// Display-friendly host for a stored website: drop the scheme, "www.", and any
// path so the picker can show a compact "stmarymh.ca" under a directory entry.
const prettyHost = (url) => {
  if (!url) return null;
  try {
    return new URL(/^https?:\/\//i.test(url) ? url : `https://${url}`).host.replace(/^www\./, "");
  } catch {
    return url
      .replace(/^https?:\/\//i, "")
      .replace(/^www\./, "")
      .replace(/\/.*$/, "");
  }
};

const InputBox = ({ label, name, value, onChange, placeholder, type = "text" }) => (
  <div className="px-4 py-3.5 bg-white rounded-[10px] shadow-[0px_0px_10px_rgba(0,0,0,0.05)] border border-gray-300 flex flex-col gap-1.5">
    <label htmlFor={name} className="text-gray-400 text-sm font-medium">
      {label}
    </label>
    <input
      id={name}
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className="text-black text-base font-medium outline-none bg-transparent"
    />
  </div>
);

/**
 * Combined Add School + (optional) Invite Admin modal.
 *
 * Two halves:
 *   1. School picker — search-or-create with explicit disambiguation. Driven
 *      by the admin-scoped /v1/schools search; selecting a match skips
 *      creation and hands the existing School back. Server-side createSchool
 *      is idempotent regardless, as a correctness backstop.
 *
 *   2. Optional admin invite — name/title/email/role. Leave blank if the
 *      admin isn't known yet (school still lands in Pending, can be invited
 *      later via View / Edit admin). All four fields together or all blank;
 *      partial fill is a validation error.
 *
 * onConfirm(school, { invited }) — `invited` is true when an invite was
 * actually sent. Parent uses it to decide whether to toast "school added"
 * vs "school added & invite sent".
 *
 * Invite-after-create rollback: if school creation succeeds but the invite
 * fails, we keep the school. The operator sees a partial-success toast and
 * can retry the invite from the Pending Schools tab. Deleting the school
 * on invite failure was rejected — it masks the real error and creates a
 * "phantom" school that appears then vanishes.
 */
const AddSchoolModal = ({ onClose, onConfirm }) => {
  const [query, setQuery] = useState("");
  // Two parallel match arrays: `operationalMatches` are existing School
  // rows (already onboarded); `directoryMatches` are SchoolDirectory
  // entries students have seeded via the freeform onboarding picker
  // that no admin has formally onboarded yet. Both come from the
  // /v1/schools/lookup endpoint in a single round trip.
  const [operationalMatches, setOperationalMatches] = useState([]);
  const [directoryMatches, setDirectoryMatches] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  // selectedSchool = operator picked an already-onboarded operational
  // School → skip create entirely. pickedDirectory = operator picked an
  // unlinked SchoolDirectory entry → create a new School AND link it to
  // that directory id (closes the dedupe gap). Mutually exclusive.
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [pickedDirectory, setPickedDirectory] = useState(null);
  // True when pickedDirectory was auto-selected by matching the entered
  // website's domain (handleFetchLogo). Drives the "Matched by website" badge
  // on the Linking-to banner; reset on any manual change so it never goes stale.
  const [matchedByWebsite, setMatchedByWebsite] = useState(false);
  const [explicitCreate, setExplicitCreate] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const dropdownRef = useRef(null);
  // Last query we AUTO-fired a web search for. Distinct from webSearchedFor
  // (which is only set on a *successful* search and also drives the manual
  // CTA/dedupe): this is set on every auto-attempt — success OR error — so a
  // failing query can't put the auto-search effect into a retry loop. Reset
  // on each query edit so a new name is eligible again.
  const autoSearchedForRef = useRef(null);

  // Optional "import logo from website" flow. The operator pastes the school
  // URL (or we prefill it from a directory/web result that carries a website),
  // fetches a logo preview, then on submit we save it to the freshly-created
  // school — so the school admin never has to upload one. `logoPreview` holds
  // { base64Data, contentType, sourceUrl } once a fetch succeeds.
  const [website, setWebsite] = useState("");
  const [logoPreview, setLogoPreview] = useState(null);
  const [fetchingLogo, setFetchingLogo] = useState(false);
  const [logoError, setLogoError] = useState(null);

  // LLM "Search the web" fallback. Surfaced only when local + directory
  // lookup return zero hits for a 3+ char query, so the operator gets a
  // useful escape hatch without bearing the OpenAI cost on every typing
  // session. webSearchedFor tracks the last query we ran a web search
  // for, so the CTA doesn't keep re-offering itself if the result was
  // genuinely empty.
  const [webSearching, setWebSearching] = useState(false);
  const [webSearchedFor, setWebSearchedFor] = useState(null);
  const [webSearchError, setWebSearchError] = useState(null);

  const [adminForm, setAdminForm] = useState({
    adminName: "",
    adminTitle: "",
    emailAddress: "",
    schoolRole: "OWNER",
  });

  const trimmed = query.trim();

  const handleQueryChange = (e) => {
    setQuery(e.target.value);
    setSelectedSchool(null);
    setPickedDirectory(null);
    setMatchedByWebsite(false);
    setExplicitCreate(false);
    setShowDropdown(true);
    // Different query → web-search CTA becomes valid again. (Without
    // this reset, typing "Hudson" → search-the-web → empty → backspace
    // to "Huds" → search-the-web wouldn't re-offer.)
    setWebSearchedFor(null);
    setWebSearchError(null);
    // New/edited query → eligible for a fresh auto web-search.
    autoSearchedForRef.current = null;
    // A different school → any logo we fetched for the previous one is stale.
    setLogoPreview(null);
    setLogoError(null);
  };

  const handleWebsiteChange = (e) => {
    setWebsite(e.target.value);
    // A changed URL invalidates the previously-fetched preview and any prior
    // website-based auto-match (the badge would otherwise go stale).
    setLogoPreview(null);
    setLogoError(null);
    setMatchedByWebsite(false);
  };

  const handleFetchLogo = async () => {
    const url = website.trim();
    if (!url || fetchingLogo) return;
    setFetchingLogo(true);
    setLogoError(null);
    try {
      const result = await schoolService.extractLogoFromUrl(url);
      setLogoPreview(result);
      setMatchedByWebsite(false); // set true below only on an actual domain match

      // Auto-resolve the school from the URL's domain so the operator doesn't
      // have to name-search — where two same-named schools collide and it's
      // easy to link the wrong one. The backend returns unlinked directory
      // entries whose website matches this exact domain, most-students-first.
      const matches = Array.isArray(result?.directoryMatches) ? result.directoryMatches : [];
      if (matches.length > 0) {
        // A directory entry's website matches this domain → link to THAT one.
        const best = matches[0];
        setSelectedSchool(null);
        setExplicitCreate(false);
        setPickedDirectory(best);
        setMatchedByWebsite(true);
        setQuery(best.name);
        setShowDropdown(false);
      } else if (result?.name && !trimmed) {
        // No directory entry shares this domain → don't risk linking to a
        // same-named-but-different entry. Only prefill an empty name field:
        // scraped metadata is a suggestion and must not overwrite an
        // operator-entered name or change an existing selection.
        setSelectedSchool(null);
        setPickedDirectory(null);
        setExplicitCreate(true);
        setQuery(result.name);
        setShowDropdown(false);
      }
      // else (no domain match, no parsed name): leave the operator's input.
    } catch (err) {
      setLogoPreview(null);
      setLogoError(err?.message || "Couldn't fetch a logo from that URL.");
    } finally {
      setFetchingLogo(false);
    }
  };

  const handleSelectExisting = (school) => {
    setSelectedSchool(school);
    setPickedDirectory(null);
    setExplicitCreate(false);
    setQuery(school.name);
    setShowDropdown(false);
  };

  const handleSelectDirectory = (entry) => {
    // Picking a directory entry means: create a new operational School
    // and link it to this directory id. We DON'T set selectedSchool —
    // that's reserved for "skip create entirely". The canonical name
    // from the directory drops into the field so the operator sees
    // exactly what'll be created.
    setSelectedSchool(null);
    setPickedDirectory(entry);
    setMatchedByWebsite(false);
    setExplicitCreate(false);
    setQuery(entry.name);
    setShowDropdown(false);
    // Directory/web results often carry the school's website — prefill it so
    // importing the logo is one click. Clear any stale preview from before.
    if (entry.website) setWebsite(entry.website);
    setLogoPreview(null);
    setLogoError(null);
  };

  const handleChooseCreate = () => {
    setSelectedSchool(null);
    setPickedDirectory(null);
    setExplicitCreate(true);
    setShowDropdown(false);
  };

  // Operator-driven LLM fallback. Backend upserts results into
  // SchoolDirectory before returning them, so the same entries become
  // available to future local lookups (admin + student). We merge into
  // directoryMatches so the existing "From directory" UI handles them
  // — no separate code path for "from web".
  const handleWebSearch = useCallback(async () => {
    if (webSearching) return;
    const q = trimmed;
    if (q.length < 3) return;
    setWebSearching(true);
    setWebSearchError(null);
    try {
      const results = await schoolService.webSearchForCreate({ q });
      // Mark the query as searched even when results are empty so the
      // CTA stops re-offering itself for the same (now-known-fruitless)
      // query.
      setWebSearchedFor(q);
      if (results.length > 0) {
        // Merge with existing directory matches, deduping by id so a
        // local entry that the LLM also surfaced doesn't render twice.
        setDirectoryMatches((prev) => {
          const existingIds = new Set(prev.map((e) => e.id));
          return [...prev, ...results.filter((r) => !existingIds.has(r.id))];
        });
      }
    } catch (err) {
      setWebSearchError(err?.message || "Web search failed.");
    } finally {
      setWebSearching(false);
    }
  }, [trimmed, webSearching]);

  // Auto-fire the web search when the cheap local + directory lookup draws a
  // blank — so the operator doesn't have to click a second button. Guards
  // that keep this from being expensive/janky:
  //   • only after the local lookup has SETTLED empty (not mid-search)
  //   • a longer idle debounce (900ms) than the local lookup's 300ms, so a
  //     half-typed name ("Queen") doesn't burn a ~3¢ web call before the
  //     operator finishes typing
  //   • autoSearchedForRef dedupes per query (incl. on error) → no retry loop
  //   • never once the operator has picked a match or chosen "create"
  // There's no resting button — a "Try again" appears only on error.
  useEffect(() => {
    if (!showDropdown) return;
    if (searching || webSearching) return;
    if (trimmed.length < 3) return;
    if (operationalMatches.length > 0 || directoryMatches.length > 0) return;
    if (selectedSchool || pickedDirectory || explicitCreate) return;
    if (autoSearchedForRef.current === trimmed || webSearchedFor === trimmed) return;

    const handler = setTimeout(() => {
      autoSearchedForRef.current = trimmed;
      handleWebSearch();
    }, 900);
    return () => clearTimeout(handler);
  }, [
    trimmed,
    searching,
    webSearching,
    operationalMatches,
    directoryMatches,
    selectedSchool,
    pickedDirectory,
    explicitCreate,
    webSearchedFor,
    showDropdown,
    handleWebSearch,
  ]);

  const handleAdminChange = (e) => {
    setAdminForm({ ...adminForm, [e.target.name]: e.target.value });
  };

  useEffect(() => {
    let isMounted = true;
    const handler = setTimeout(async () => {
      if (!showDropdown || !trimmed) {
        if (isMounted) {
          setOperationalMatches([]);
          setDirectoryMatches([]);
        }
        return;
      }
      try {
        setSearching(true);
        const response = await schoolService.lookupForCreate({ q: trimmed, limit: 10 });
        if (!isMounted) return;
        setOperationalMatches(response?.operational ?? []);
        setDirectoryMatches(response?.directory ?? []);
      } catch (err) {
        // Treat a failed lookup as "no matches" — the create path's server
        // idempotency still prevents duplicates if a match was missed.
        if (isMounted) {
          setOperationalMatches([]);
          setDirectoryMatches([]);
        }
      } finally {
        if (isMounted) setSearching(false);
      }
    }, 300);

    return () => {
      isMounted = false;
      clearTimeout(handler);
    };
  }, [trimmed, showDropdown]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Exact match across BOTH sources — if the typed name already exists
  // operationally or in the directory, hide the "Create new" affordance
  // so the operator picks the canonical row instead of forking it.
  const lowerTrimmed = trimmed.toLowerCase();
  const exactMatch =
    operationalMatches.find((m) => m.name.trim().toLowerCase() === lowerTrimmed) ||
    directoryMatches.find((m) => m.name.trim().toLowerCase() === lowerTrimmed);

  let mode = "idle";
  if (selectedSchool) {
    mode = "selected";
  } else if (pickedDirectory) {
    // Treated as create (we'll create a new operational School), but
    // with the directory link applied. See handleSubmit.
    mode = "create";
  } else if (explicitCreate) {
    mode = "create";
  } else if (trimmed && !searching && operationalMatches.length === 0 && directoryMatches.length === 0) {
    mode = "create";
  }

  // Admin section state: all-filled / all-blank / partial.
  const adminFilled = {
    name: adminForm.adminName.trim().length > 0,
    title: adminForm.adminTitle.trim().length > 0,
    email: adminForm.emailAddress.trim().length > 0,
  };
  const adminFilledCount = Object.values(adminFilled).filter(Boolean).length;
  const wantsInvite = adminFilledCount === 3;
  const adminPartial = adminFilledCount > 0 && adminFilledCount < 3;

  // Only flag the email once something's typed in it — an all-blank admin
  // section is a valid "add school without an invite" and shouldn't show a
  // typo hint.
  const emailError = adminForm.emailAddress.trim() ? inviteEmailError(adminForm.emailAddress) : null;

  const canSubmit =
    (mode === "selected" || mode === "create") && !adminPartial && !emailError && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setError(null);

    // Phase A — resolve school (create if needed).
    let school = selectedSchool;
    if (mode === "create") {
      setSubmitting(true);
      try {
        // pickedDirectory?.id is forwarded only when the operator
        // chose a directory entry; otherwise the new School is a
        // fresh, unlinked row. Backend rejects a duplicate link
        // attempt (409) — surfaced inline below.
        school = await schoolService.createSchool(trimmed, pickedDirectory?.id);
      } catch (err) {
        setError(err.message || "Failed to add school. Please try again.");
        setSubmitting(false);
        return;
      }
    }

    // Phase A.5 — optional logo import. Best-effort: the school is already
    // saved, so a logo failure never rolls it back (same philosophy as the
    // invite step below). `logoResult` is merged into every onConfirm call so
    // the parent toast can note the outcome.
    let logoResult = {};
    if (logoPreview && school?.id) {
      setSubmitting(true);
      try {
        await schoolService.saveSchoolLogo(school.id, {
          base64Data: logoPreview.base64Data,
          contentType: logoPreview.contentType,
        });
        logoResult = { logoImported: true };
      } catch (err) {
        logoResult = { logoError: err?.message || "Logo import failed." };
      }
    }

    // Phase B — optional invite. School is already saved at this point;
    // any failure here is reported but does NOT roll back the school.
    if (wantsInvite) {
      setSubmitting(true);
      try {
        const response = await schoolService.inviteSchoolStaff(school.id, {
          email: adminForm.emailAddress.trim(),
          role: adminForm.schoolRole,
          name: adminForm.adminName.trim(),
          title: adminForm.adminTitle.trim(),
        });
        if (!response.success) {
          const detail = response.error?.detail || response.message || "Invite failed.";
          // Hand the school back so the parent updates its lists, but
          // surface the partial failure via the second arg so the toast
          // explains exactly what happened.
          onConfirm?.(school, { invited: false, inviteError: detail, ...logoResult });
          onClose();
          return;
        }
      } catch (err) {
        onConfirm?.(school, {
          invited: false,
          inviteError: err.message || "Invite failed.",
          ...logoResult,
        });
        onClose();
        return;
      }
    }

    onConfirm?.(school, { invited: wantsInvite, ...logoResult });
    onClose();
  };

  const submitLabel = submitting
    ? wantsInvite
      ? "Adding & inviting..."
      : "Adding..."
    : wantsInvite
      ? mode === "selected"
        ? "Use school & send invite"
        : "Add school & send invite"
      : mode === "selected"
        ? "Use school"
        : "Add school";

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex justify-center items-center">
      <div className="w-[560px] max-h-[90vh] bg-white rounded-[20px] overflow-hidden font-['Manrope'] flex flex-col">
        <div className="p-6 bg-gradient-to-br from-white to-blue-100 border-b border-blue-100 flex justify-between items-center flex-shrink-0">
          <h2 className="text-[18px] font-bold text-gray-900">Add School</h2>
          <button type="button" onClick={onClose} className="p-1">
            <img src={closeIcon} alt="Close" className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 flex flex-col gap-6 overflow-y-auto">
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-800 text-sm font-medium">{error}</p>
            </div>
          )}

          <div className="flex flex-col gap-2">
            <div ref={dropdownRef} className="relative">
              <InputBox
                label="School Name"
                name="schoolName"
                value={query}
                onChange={handleQueryChange}
                placeholder="Search or type a school name..."
              />
              {showDropdown && trimmed && (
                <div className="absolute z-10 mt-2 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-80 overflow-auto">
                  {searching ? (
                    <div className="px-4 py-3 text-sm text-gray-500">Searching...</div>
                  ) : (
                    <>
                      {/* ── Operational: already onboarded ── */}
                      {operationalMatches.length > 0 && (
                        <>
                          <div className="px-4 pt-2 pb-1 text-[10px] uppercase tracking-wide text-gray-400 font-semibold">
                            Onboarded schools
                          </div>
                          {operationalMatches.map((m) => (
                            <button
                              key={`op-${m.id}`}
                              type="button"
                              onClick={() => handleSelectExisting(m)}
                              className="w-full text-left px-4 py-2.5 hover:bg-blue-50 flex items-center justify-between gap-3"
                            >
                              <span className="text-sm text-gray-800">{m.name}</span>
                              {m.schoolAdmin ? (
                                <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-100 text-emerald-800 whitespace-nowrap">
                                  Has admin
                                </span>
                              ) : (
                                <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-[#FEF3C7] text-amber-800 whitespace-nowrap">
                                  No admin yet
                                </span>
                              )}
                            </button>
                          ))}
                        </>
                      )}

                      {/* ── Directory: discovered by students, not yet onboarded ── */}
                      {directoryMatches.length > 0 && (
                        <>
                          <div className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-wide text-gray-400 font-semibold border-t border-gray-100 mt-1">
                            From directory — not yet onboarded
                          </div>
                          {directoryMatches.map((m) => {
                            const place = [m.city, m.region, m.country].filter(Boolean).join(", ");
                            const site = prettyHost(m.website);
                            return (
                              <button
                                key={`dir-${m.id}`}
                                type="button"
                                onClick={() => handleSelectDirectory(m)}
                                className="w-full text-left px-4 py-2.5 hover:bg-blue-50 flex items-center justify-between gap-3"
                              >
                                <span className="flex flex-col">
                                  <span className="text-sm text-gray-800">{m.name}</span>
                                  {place && (
                                    <span className="text-[11px] text-gray-500">{place}</span>
                                  )}
                                  {site && <span className="text-[11px] text-blue-600">{site}</span>}
                                </span>
                                <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-gray-100 text-gray-700 whitespace-nowrap">
                                  {m.studentCount > 0
                                    ? `${m.studentCount} student${m.studentCount === 1 ? "" : "s"}`
                                    : "Discovered"}
                                </span>
                              </button>
                            );
                          })}
                        </>
                      )}

                      {operationalMatches.length === 0 && directoryMatches.length === 0 && (
                        <div className="px-4 py-2.5 text-sm text-gray-500">
                          No existing school matches "{trimmed}".
                        </div>
                      )}

                      {/* Web search fires automatically when the cheap local
                          lookup draws a blank (see the auto-search effect) —
                          no resting button. While it runs we show a pure
                          indicator; a "Try again" only appears on error. */}
                      {webSearching && (
                        <div className="w-full px-4 py-2.5 border-t border-gray-100 text-sm text-blue-600 font-medium flex items-center gap-2">
                          <span className="inline-block w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                          Searching the web for "{trimmed}"…
                        </div>
                      )}

                      {webSearchedFor === trimmed && directoryMatches.length === 0 && !webSearching && (
                        <div className="px-4 py-2 text-xs text-gray-500 border-t border-gray-100">
                          Web search didn't find anything for "{trimmed}".
                        </div>
                      )}

                      {webSearchError && !webSearching && (
                        <div className="px-4 py-2 border-t border-gray-100 flex items-center justify-between gap-3">
                          <span className="text-xs text-red-600">{webSearchError}</span>
                          <button
                            type="button"
                            onClick={handleWebSearch}
                            className="text-xs font-semibold text-blue-600 hover:text-blue-700 whitespace-nowrap"
                          >
                            Try again
                          </button>
                        </div>
                      )}

                      {!exactMatch && (
                        <button
                          type="button"
                          onClick={handleChooseCreate}
                          className="w-full text-left px-4 py-2.5 border-t border-gray-100 hover:bg-blue-50 text-sm text-blue-600 font-medium"
                        >
                          Create new school: "{trimmed}"
                        </button>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>

            {mode === "selected" && (
              <div className="px-4 py-3 bg-blue-50 rounded-[10px] border border-blue-100 flex flex-col gap-1">
                <p className="text-gray-900 text-sm font-semibold">
                  Selected: {selectedSchool.name}
                </p>
                {selectedSchool.schoolAdmin ? (
                  <p className="text-amber-800 text-xs">
                    This school already has an admin
                    {selectedSchool.schoolAdmin.name
                      ? ` (${selectedSchool.schoolAdmin.name})`
                      : ""}
                    . Filling in admin details below sends an invite for an
                    additional admin or staff member.
                  </p>
                ) : (
                  <p className="text-gray-500 text-xs">
                    No admin yet — fill in details below to invite one now,
                    or leave blank to add it later.
                  </p>
                )}
              </div>
            )}

            {mode === "create" && pickedDirectory && (
              <div className="px-4 py-3 bg-blue-50 rounded-[10px] border border-blue-100 flex flex-col gap-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-gray-900 text-sm font-semibold">Linking to: {pickedDirectory.name}</p>
                  {matchedByWebsite && (
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-100 text-blue-700 whitespace-nowrap">
                      Matched by website
                    </span>
                  )}
                </div>
                <p className="text-gray-500 text-xs">
                  This school was already in our directory from {pickedDirectory.studentCount} student
                  {pickedDirectory.studentCount === 1 ? "" : "s"}. Creating the operational record now
                  and linking the two so they share an identity going forward.
                </p>
              </div>
            )}

            {mode === "create" && !pickedDirectory && (
              <p className="text-gray-500 text-xs px-1">
                Creates "{trimmed}" now. Fill in admin details below to send
                an invite at the same time, or leave blank to add the admin
                later.
              </p>
            )}

            {mode === "idle" && (
              <p className="text-gray-500 text-xs px-1">
                Select an existing school above, or choose "Create new" to add it.
              </p>
            )}
          </div>

          {/* ── Optional: import logo from the school's website ──────────── */}
          <div className="flex flex-col gap-2">
            <div className="px-4 py-3.5 bg-white rounded-[10px] shadow-[0px_0px_10px_rgba(0,0,0,0.05)] border border-gray-300 flex flex-col gap-1.5">
              <label htmlFor="website" className="text-gray-400 text-sm font-medium">
                School website <span className="text-gray-300">— optional, imports their logo</span>
              </label>
              <div className="flex items-center gap-2">
                <input
                  id="website"
                  type="url"
                  value={website}
                  onChange={handleWebsiteChange}
                  placeholder="e.g., hudsoncollege.edu"
                  className="flex-1 min-w-0 text-black text-base font-medium outline-none bg-transparent"
                />
                <button
                  type="button"
                  onClick={handleFetchLogo}
                  disabled={!website.trim() || fetchingLogo}
                  className="px-3 py-1.5 text-sm font-medium text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 whitespace-nowrap"
                >
                  {fetchingLogo ? (
                    <>
                      <span className="inline-block w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                      Fetching…
                    </>
                  ) : (
                    "Fetch logo"
                  )}
                </button>
              </div>
            </div>

            {logoError && <p className="text-amber-700 text-xs px-1">{logoError}</p>}

            {logoPreview && (
              <div className="px-4 py-3 bg-blue-50 rounded-[10px] border border-blue-100 flex items-center gap-3">
                <img
                  src={`data:${logoPreview.contentType};base64,${logoPreview.base64Data}`}
                  alt="School logo preview"
                  className="w-12 h-12 rounded-lg object-contain bg-white border border-gray-200 flex-shrink-0"
                />
                <div className="flex flex-col gap-0.5 min-w-0">
                  <p className="text-gray-900 text-sm font-semibold">Logo found</p>
                  <p className="text-gray-500 text-xs">
                    Saved to the school when you click "{mode === "selected" ? "Use school" : "Add school"}".
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setLogoPreview(null);
                    setLogoError(null);
                  }}
                  className="ml-auto text-gray-400 hover:text-gray-600 text-xs font-medium flex-shrink-0"
                >
                  Remove
                </button>
              </div>
            )}
          </div>

          {/* ── Optional admin section ─────────────────────────────────── */}
          <div className="flex flex-col gap-3">
            <div className="flex items-baseline justify-between">
              <h3 className="text-gray-900 text-base font-semibold">First admin</h3>
              <span className="text-gray-400 text-xs font-medium">
                Optional — leave blank if unknown
              </span>
            </div>

            <div className="flex flex-col gap-4">
              <InputBox
                label="Admin Name (add more admins in school details after saving)"
                name="adminName"
                value={adminForm.adminName}
                onChange={handleAdminChange}
                placeholder="e.g., John Doe"
              />
              <InputBox
                label="Admin Title"
                name="adminTitle"
                value={adminForm.adminTitle}
                onChange={handleAdminChange}
                placeholder="e.g., Principal or Head of Department"
              />
              <div className="flex flex-col gap-1">
                <InputBox
                  label="Email Address"
                  name="emailAddress"
                  value={adminForm.emailAddress}
                  onChange={handleAdminChange}
                  type="email"
                  placeholder="e.g., admin@school.edu"
                />
                {emailError && <p className="px-1 text-sm text-red-500">{emailError}</p>}
              </div>
              <div className="px-4 py-3.5 bg-white rounded-[10px] shadow-[0px_0px_10px_rgba(0,0,0,0.05)] border border-gray-300 flex flex-col gap-1.5">
                <label htmlFor="schoolRole" className="text-gray-400 text-sm font-medium">
                  Initial Role
                </label>
                <select
                  id="schoolRole"
                  name="schoolRole"
                  value={adminForm.schoolRole}
                  onChange={handleAdminChange}
                  className="text-black text-base font-medium outline-none bg-transparent"
                >
                  <option value="OWNER">Owner</option>
                  <option value="ADMIN">Admin</option>
                </select>
              </div>
            </div>

            {adminPartial && (
              <p className="text-amber-700 text-xs px-1">
                Fill in all admin fields to send an invite, or clear them all
                to skip the invite for now.
              </p>
            )}
          </div>

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="h-11 px-4 text-[16px] font-medium text-gray-900 border border-blue-500 rounded-lg hover:bg-blue-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="h-11 px-4 text-[16px] font-medium text-white rounded-lg bg-blue-500 hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddSchoolModal;
